// frontend/src/hooks/useTimelineData.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI, confidentialAPI, configAPI } from '../services/api';
import toast from '../utils/toast';

/**
 * Milestone-type HR letter template codes.
 * Letters with these codes are career milestones (promotion, confirmation, etc.).
 * Administrative letters (employment_verification, pr_support, home_leave) are excluded.
 */
export const MILESTONE_TEMPLATE_CODES = new Set([
  'PROMO', 'PROMOTION', 'PROM',
  'INC', 'INCREMENT', 'SALARY_INC', 'PAY_INC',
  'TRANSFER', 'TRANS',
  'CONFIRM', 'CONFIRMATION',
  'APPOINT', 'APPOINTMENT',
]);

/**
 * Check if an HR letter's template_code represents a career milestone.
 * Uses partial matching (e.g. "PROMO_2024" matches "PROMO").
 */
export const isMilestoneLetterCode = (templateCode) => {
  if (!templateCode) return false;
  const upper = templateCode.toUpperCase();
  if (MILESTONE_TEMPLATE_CODES.has(upper)) return true;
  for (const code of MILESTONE_TEMPLATE_CODES) {
    if (upper.includes(code)) return true;
  }
  return false;
};

// ── P1: HR Letters (admin – fetches by staff_id from confidential API) ──────
export const useEmployeeHRLetters = (staffId) => {
  return useQuery({
    queryKey: ['timeline', 'hr-letters', staffId],
    queryFn: async () => {
      const response = await confidentialAPI.getHRLetters({
        staff_id: staffId,
        ordering: '-generated_at',
      });
      return response.data?.results || response.data || [];
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000, // 10 minutes – letters rarely change
  });
};

// ── P1: HR Letters (self-service – returns current user's own letters) ───────
export const useMyHRLetters = (enabled = true, options = {}) => {
  return useQuery({
    queryKey: ['timeline', 'my-hr-letters'],
    queryFn: async () => {
      // combinedAPI spreads confidentialAPI, so api.getHRLetters works.
      // Backend scopes to the authenticated user via HRLetterViewSet's
      // get_queryset() — non-HR/admin callers only see their own letters.
      const response = await confidentialAPI.getHRLetters({
        ordering: '-generated_at',
      });
      return response.data?.results || response.data || [];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
};

// ── P2: Professional Certificates ───────────────────────────────────────────
export const useEmployeeCertificates = (staffId) => {
  return useQuery({
    queryKey: ['timeline', 'certificates', staffId],
    queryFn: async () => {
      const response = await staffAPI.getCertificates({ cert_staff_id: staffId });
      return response.data?.results || response.data || [];
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000,
  });
};

// ── P3: Training Enrollments (completed) ────────────────────────────────────
export const useEmployeeTrainingEnrollments = (staffId) => {
  return useQuery({
    queryKey: ['timeline', 'training', staffId],
    queryFn: async () => {
      const response = await configAPI.getTrainingEnrollments({
        employee_staff_id: staffId,
        enrollment_status: 'completed',
      });
      return response.data?.results || response.data || [];
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000,
  });
};

// ── P4: Education Records ───────────────────────────────────────────────────
export const useEmployeeEducation = (staffId) => {
  return useQuery({
    queryKey: ['timeline', 'education', staffId],
    queryFn: async () => {
      const response = await staffAPI.getEducation({ education_staff_id: staffId });
      return response.data?.results || response.data || [];
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000,
  });
};

// ── P5: Work Experience ─────────────────────────────────────────────────────
export const useEmployeeWorkExperience = (staffId) => {
  return useQuery({
    queryKey: ['timeline', 'work-experience', staffId],
    queryFn: async () => {
      const response = await staffAPI.getWorkExperience({ work_exp_staff_id: staffId });
      return response.data?.results || response.data || [];
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000,
  });
};

const invalidateWorkExperienceQueries = (queryClient, staffId) => {
  queryClient.invalidateQueries({ queryKey: ['timeline', 'work-experience'] });
  if (staffId) {
    queryClient.invalidateQueries({ queryKey: ['timeline', 'work-experience', staffId] });
  }
};

const workExperienceErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data[0];
  if (data?.detail) return data.detail;
  if (data?.error) return data.error;
  if (data && typeof data === 'object') {
    const first = Object.values(data)[0];
    if (typeof first === 'string') return first;
    if (Array.isArray(first) && first[0]) return first[0];
  }
  return fallback;
};

export const useCreateWorkExperience = (staffId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.createWorkExperience(
      staffId ? { ...data, work_exp_staff_id: staffId } : data
    ),
    onSuccess: () => {
      invalidateWorkExperienceQueries(queryClient, staffId);
      toast.success('Work experience added');
    },
    onError: (error) => {
      toast.error(workExperienceErrorMessage(error, 'Failed to add work experience'));
    },
  });
};

export const useUpdateWorkExperience = (staffId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.updateWorkExperience(id, data),
    onSuccess: () => {
      invalidateWorkExperienceQueries(queryClient, staffId);
      toast.success('Work experience updated');
    },
    onError: (error) => {
      toast.error(workExperienceErrorMessage(error, 'Failed to update work experience'));
    },
  });
};

export const useDeleteWorkExperience = (staffId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => staffAPI.deleteWorkExperience(id),
    onSuccess: () => {
      invalidateWorkExperienceQueries(queryClient, staffId);
      toast.success('Work experience deleted');
    },
    onError: (error) => {
      toast.error(workExperienceErrorMessage(error, 'Failed to delete work experience'));
    },
  });
};

// ── P6: Employment Movements (promotions, transfers, redesignations) ────────
export const useEmployeeMovements = (staffId) => {
  return useQuery({
    queryKey: ['timeline', 'employment-movements', staffId],
    queryFn: async () => {
      const response = await staffAPI.getEmploymentMovements(staffId);
      return response.data || [];
    },
    enabled: !!staffId,
    staleTime: 5 * 60 * 1000,
  });
};
